// src/utils/silentPrintService.js
// Silent printing service for packing slips
// Uses hidden iframe technique to print without showing browser print dialog

/**
 * Print HTML content silently using hidden iframe
 * @param {string} html - HTML content to print
 * @param {Object} options - Print options
 * @returns {Promise<void>}
 */
export async function printHTMLSilent(html, options = {}) {
    return new Promise((resolve, reject) => {
        let settled = false;
        let iframe = null;
        let timeoutId = null;
        const finish = (result, error) => {
            if (settled) return;
            settled = true;
            if (timeoutId) clearTimeout(timeoutId);
            setTimeout(() => {
                try {
                    iframe?.remove();
                } catch (cleanupError) {
                    console.warn('[SilentPrint] Error removing iframe:', cleanupError);
                }
            }, options.removeDelay || 1000);
            if (error) reject(error);
            else resolve(result);
        };
        try {
            const {
                removeDelay = 1000,
                onBeforePrint = null,
                onAfterPrint = null
            } = options;
            
            // Create hidden iframe
            if (typeof html !== 'string' || !html.trim()) {
                throw new Error('No printable HTML was provided.');
            }
            iframe = document.createElement('iframe');
            iframe.id = `print-frame-${Date.now()}`;
            iframe.style.position = 'fixed';
            iframe.style.right = '0';
            iframe.style.bottom = '0';
            iframe.style.width = '0';
            iframe.style.height = '0';
            iframe.style.border = 'none';
            iframe.style.visibility = 'hidden';
            
            iframe.onload = () => {
                try {
                    if (onBeforePrint) {
                        onBeforePrint();
                    }
                    
                    console.log('[SilentPrint] Triggering print...');
                    
                    // Trigger print
                    iframe.contentWindow.focus();
                    const printWindow = iframe.contentWindow;
                    const complete = () => {
                        if (onAfterPrint) onAfterPrint();
                        finish({
                            status: 'dialog-closed-unknown',
                            transport: 'browser',
                            message: 'Browser print dialog closed; physical output is not automatically verifiable.',
                        });
                    };
                    printWindow.addEventListener?.('afterprint', complete, { once: true });
                    printWindow.print();
                    setTimeout(complete, Math.max(500, removeDelay));
                } catch (error) {
                    console.error('[SilentPrint] Error during print:', error);
                    finish(null, error);
                }
            };
            
            iframe.onerror = (error) => {
                console.error('[SilentPrint] Iframe error:', error);
                finish(null, error instanceof Error ? error : new Error('Print frame failed to load.'));
            };
            timeoutId = setTimeout(
                () => finish(null, new Error('Browser print preparation timed out.')),
                Number(options.timeoutMs) || 15000,
            );
            console.log('[SilentPrint] Created hidden iframe for printing');
            iframe.srcdoc = html;
            document.body.appendChild(iframe);
        } catch (error) {
            console.error('[SilentPrint] Error creating print iframe:', error);
            finish(null, error);
        }
    });
}

/**
 * Print packing slip silently
 * @param {Function} buildHTMLFunction - Function that generates HTML
 * @param {Array} orders - Orders to print
 * @param {Object} options - Additional options
 * @returns {Promise<void>}
 */
export async function printPackingSlipSilent(buildHTMLFunction, orders, options = {}) {
    try {
        console.log('[SilentPrint] Generating packing slip HTML for orders:', {
            count: orders.length,
            orderIds: orders.map(o => o.orderId || o.id)
        });
        
        // Generate HTML using provided function
        const html = await Promise.resolve(buildHTMLFunction(orders, options.logoUrl));
        
        if (!html) {
            throw new Error('Failed to generate HTML for packing slip');
        }
        
        console.log('[SilentPrint] Generated HTML:', {
            length: html.length,
            preview: html.substring(0, 200)
        });
        
        // Print silently
        await printHTMLSilent(html, {
            removeDelay: options.removeDelay || 1000,
            onBeforePrint: () => {
                console.log('[SilentPrint] Starting print for packing slip');
                if (options.onBeforePrint) {
                    options.onBeforePrint();
                }
            },
            onAfterPrint: () => {
                console.log('[SilentPrint] Completed print for packing slip');
                if (options.onAfterPrint) {
                    options.onAfterPrint();
                }
            }
        });
        
        console.log('[SilentPrint] Print completed successfully');
        
    } catch (error) {
        console.error('[SilentPrint] Error printing packing slip:', error);
        throw error;
    }
}

/**
 * Print multiple packing slips in sequence
 * @param {Function} buildHTMLFunction - Function that generates HTML
 * @param {Array} ordersArray - Array of order arrays (each array is one slip)
 * @param {Object} options - Additional options
 * @returns {Promise<void>}
 */
export async function printMultiplePackingSlips(buildHTMLFunction, ordersArray, options = {}) {
    try {
        const delay = options.delayBetweenPrints || 500;
        
        console.log('[SilentPrint] Printing multiple packing slips:', {
            count: ordersArray.length
        });
        
        for (let i = 0; i < ordersArray.length; i++) {
            const orders = ordersArray[i];
            
            console.log(`[SilentPrint] Printing slip ${i + 1} of ${ordersArray.length}`);
            
            await printPackingSlipSilent(buildHTMLFunction, orders, options);
            
            // Wait between prints
            if (i < ordersArray.length - 1) {
                await new Promise(resolve => setTimeout(resolve, delay));
            }
        }
        
        console.log('[SilentPrint] All packing slips printed');
        
    } catch (error) {
        console.error('[SilentPrint] Error printing multiple slips:', error);
        throw error;
    }
}

/**
 * Check if browser supports silent printing
 * @returns {boolean}
 */
export function isSilentPrintSupported() {
    // Check if we can create iframes and access contentWindow
    try {
        const testFrame = document.createElement('iframe');
        return !!(testFrame.contentWindow);
    } catch {
        return false;
    }
}

/**
 * Show notification about browser compatibility
 * @returns {string} Compatibility message
 */
export function getBrowserCompatibilityMessage() {
    const userAgent = navigator.userAgent.toLowerCase();
    
    if (userAgent.includes('chrome') || userAgent.includes('chromium')) {
        return 'Silent printing fully supported in Chrome';
    } else if (userAgent.includes('edge')) {
        return 'Silent printing fully supported in Edge';
    } else if (userAgent.includes('firefox')) {
        return 'Firefox may show a brief print preview';
    } else if (userAgent.includes('safari')) {
        return 'Safari may show a brief print preview';
    } else {
        return 'Silent printing support varies by browser';
    }
}

const silentPrintService = {
    printHTMLSilent,
    printPackingSlipSilent,
    printMultiplePackingSlips,
    isSilentPrintSupported,
    getBrowserCompatibilityMessage
};

export default silentPrintService;

